import * as React from "react"
import { Link as GatsbyLink, withPrefix } from "gatsby"
import { translations, type Dictionary } from "./translations"

export type Lang = "en" | "ro"

export const LANGS: Lang[] = ["en", "ro"]

export const LANG_LABELS: Record<Lang, string> = {
  en: "EN",
  ro: "RO",
}

export const LANG_NAMES: Record<Lang, string> = {
  en: "English",
  ro: "Română",
}

// Default (unprefixed) language served at "/"
export const DEFAULT_LANG: Lang = "en"

interface LangContextValue {
  lang: Lang
  t: Dictionary
}

const LangContext = React.createContext<LangContextValue>({
  lang: DEFAULT_LANG,
  t: translations[DEFAULT_LANG],
})

export const LangProvider: React.FC<{
  lang?: Lang
  children: React.ReactNode
}> = ({ lang, children }) => {
  const resolved: Lang = lang === "ro" ? "ro" : "en"
  return (
    <LangContext.Provider
      value={{ lang: resolved, t: translations[resolved] }}
    >
      {children}
    </LangContext.Provider>
  )
}

export function useLang(): Lang {
  return React.useContext(LangContext).lang
}

/** Returns the translation dictionary for the active language. */
export function useT(): Dictionary {
  return React.useContext(LangContext).t
}

/** Prefix an internal path with the active language (e.g. /ro/services). */
export function localizePath(path: string, lang: Lang): string {
  if (lang === DEFAULT_LANG) return path
  if (path === "/") return "/ro/"
  return `/ro${path.startsWith("/") ? path : `/${path}`}`
}

/** Swap the current pathname to another language, preserving the route. */
export function switchLangPath(pathname: string, target: Lang): string {
  // Strip any gatsby path prefix, then any leading /ro
  let p = pathname
  const prefix = withPrefix("/")
  if (prefix !== "/" && p.startsWith(prefix)) {
    p = "/" + p.slice(prefix.length)
  }
  const stripped = p.replace(/^\/ro(\/|$)/, "/")
  return localizePath(stripped === "" ? "/" : stripped, target)
}

type LinkProps = Omit<React.ComponentProps<typeof GatsbyLink>, "ref"> & {
  to: string
}

/** A Gatsby Link that automatically prefixes the active language. */
export const Link: React.FC<LinkProps> = ({ to, ...rest }) => {
  const lang = useLang()
  const isInternal = /^\/(?!\/)/.test(to)
  const target = isInternal ? localizePath(to, lang) : to
  return <GatsbyLink to={target} {...rest} />
}

export { translations }
export type { Dictionary }
